import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { classNames } from "../../../../helpers/classNames";
import { useQuery } from "@tanstack/react-query";
import { postFrontList } from "../../../../services/postService";
import { toast } from "react-toastify";
import { lngDetect } from "../../../common/lngDetect";

const Faq = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [filters] = useState({
    limit: 100,
    page: 1,
    sortQuery: "-createdAt",
  });

  const {
    data: faqData,
    error,
    isLoading,
    updateData,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["front-faq", filters],
    queryFn: async () => {
      const res = await postFrontList({
        ...filters,
        type: "faq",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const activeFaqs = {
    title: "Frequently Asked Questions",
    faqData: faqData?.data.map((item) => ({
      _id: item?._id,
      status: item?.status,
      title: lngDetect(item?.content)?.title,
      description: lngDetect(item?.content)?.subTitle,
    })),
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <section className="relative py-10 xls:py-24 z-0 overflow-hidden" id="faq">
      {/* Background Elements */}
      <motion.div
        animate={{ opacity: [0.15, 0.25, 0.15], scale: [1, 1.15, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 right-0 w-[600px] h-[600px] bg-electric-violet/30 rounded-full blur-[120px] z-[-1]"
      />

      <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1], scale: [1.1, 1, 1.1] }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
        className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-yellow-400/20 rounded-full blur-[100px] z-[-1]"
      />

      <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl mx-auto px-4">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Header */}
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-20">
            <motion.h2
              variants={item}
              className="text-2xl sm:text-3xl xl:text-4xl font-bold leading-normal "
            >
              {activeFaqs.title}
            </motion.h2>
          </div>

          {/* FAQ Grid - Two Column Layout */}
          <div className="grid grid-cols-1 gap-6 max-w-7xl mx-auto">
            {activeFaqs?.faqData?.map((faq, index) => {
              const isOpen = openIndex === index;
              const isEven = index % 2 === 0;

              return (
                <motion.div key={faq._id} variants={item} className="h-full">
                  <motion.div
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className={classNames(
                      "relative h-full py-4 pl-24 pr-4 rounded-xl cursor-pointer transition-all duration-500 overflow-hidden group",
                      isOpen
                        ? "bg-gradient-to-br from-electric-violet/10 to-transparent border-2 border-electric-violet shadow-2xl shadow-electric-violet/20"
                        : "bg-white/[0.02] border border-white/10 hover:border-white/20 hover:bg-white/[0.04]"
                    )}
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Accent Line */}
                    <div
                      className={classNames(
                        "absolute top-0 left-0 right-0 h-1 transition-all duration-500",
                        isOpen
                          ? "bg-gradient-to-r from-electric-violet via-purple-500 to-electric-violet"
                          : "bg-gradient-to-r from-transparent via-white/10 to-transparent"
                      )}
                    />

                    {/* Content */}
                    <div className="relative space-y-4">
                      {/* Question */}
                      <div className="flex items-start justify-between gap-4">
                        <h3
                          className={classNames(
                            "text-xl font-bold leading-tight transition-colors duration-300",
                            isOpen ? "text-white" : "text-white/90"
                          )}
                        >
                          {faq.title}
                        </h3>

                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{
                            duration: 0.4,
                            ease: [0.22, 1, 0.36, 1],
                          }}
                          className={classNames(
                            "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300",
                            isOpen
                              ? "bg-electric-violet text-white shadow-lg shadow-electric-violet/50"
                              : "bg-white/5 text-white/40 group-hover:bg-white/10"
                          )}
                        >
                          <i className="fa-solid fa-chevron-down text-sm"></i>
                        </motion.div>
                      </div>

                      {/* Answer */}
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{
                              opacity: 1,
                              height: "auto",
                              transition: {
                                height: {
                                  duration: 0.4,
                                  ease: [0.22, 1, 0.36, 1],
                                },
                                opacity: { duration: 0.3, delay: 0.1 },
                              },
                            }}
                            exit={{
                              opacity: 0,
                              height: 0,
                              transition: {
                                height: {
                                  duration: 0.3,
                                  ease: [0.22, 1, 0.36, 1],
                                },
                                opacity: { duration: 0.2 },
                              },
                            }}
                            className="overflow-hidden"
                          >
                            <p className="text-base xl:text-lg text-white/70 leading-relaxed pt-2">
                              {faq.description}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Number Badge */}
                      <div
                        className={classNames(
                          "absolute -top-4 -left-20 w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg transition-all duration-500 rotate-12",
                          isOpen
                            ? "bg-gradient-to-br from-electric-violet to-purple-600 text-white shadow-xl"
                            : "bg-white/5 text-white/20 group-hover:bg-white/10"
                        )}
                      >
                        {index + 1}
                      </div>
                    </div>

                    {/* Glow Effect */}
                    {isOpen && (
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-electric-violet/5 to-transparent rounded-3xl pointer-events-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      />
                    )}
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Faq;
